import styled, { css } from "styled-components";

export type FlexProps = {
  /**
   * The direction that the flex items are laid out in.
   * @default row
   */
  direction?: "row" | "column" | "row-reverse" | "column-reverse";

  /**
   * Spacing between flex items.
   * @default 0
   */
  gap?: number;

  /**
   * Alignment of flex items along the cross axis.
   * @default 'center' when horizontal and 'start' when vertical.
   */
  align?: "start" | "center" | "end" | "stretch" | "baseline";

  /**
   * How items within the flex container are organized on the main axis.
   * @default start
   */
  justify?: "start" | "center" | "end" | "between" | "around";

  /**
   * Whether the flex container should wrap its items to a new row/column when
   * it expands beyond its available size.
   * @default false
   */
  wrap?: boolean;
};

const FLEX_PROP_KEYS = new Set([
  "direction",
  "gap",
  "justify",
  "align",
  "wrap",
] as const);

// statically assert that all the keys are present in `FLEX_PROP_KEYS`
type MissingKeys = Exclude<
  keyof FlexProps,
  NonNullable<
    ReturnType<ReturnType<(typeof FLEX_PROP_KEYS)["keys"]>["next"]>["value"]
  >
>;
null as MissingKeys satisfies never; // if this fails, add to `FLEX_PROP_KEYS`

/** Container component for composing flexbox layouts. */
// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- component def
export const Flex = styled.div.withConfig<FlexProps>({
  shouldForwardProp(prop, defaultValidatorFn) {
    // consume all our defined props
    // we could instead define all our props with a `$` prefix, but:
    // 1. given the whole point of this component is to make typing a
    //    flex container quicker, saving the special character prefix is nice
    // 2. this component reserves the right to not use `styled-components` in
    //    the future, so we'd rather avoid that prefix convention making it
    //    to the public API
    if ((FLEX_PROP_KEYS as Set<string>).has(prop)) {
      return false;
    }
    return defaultValidatorFn(prop);
  },
})`
  display: flex;
  flex-direction: ${({ direction = "row" }) => direction};

  align-items: ${({
    direction = "row",
    align = direction === "row" || direction === "row-reverse"
      ? "center"
      : "start",
  }) => FLEX_ALIGNMENT_MAP[align]};

  justify-content: ${({ justify = "start" }) => FLEX_ALIGNMENT_MAP[justify]};

  flex-wrap: ${({ wrap = false }) => (wrap ? "wrap" : "nowrap")};

  ${({ gap }) =>
    gap != null &&
    css`
      gap: ${gap}px;
    `}
`;

const FLEX_ALIGNMENT_MAP: Record<
  NonNullable<FlexProps["justify"] | FlexProps["align"]>,
  string
> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
  stretch: "stretch",
  baseline: "baseline",
};
